import React, { useMemo, useState } from 'react';

import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import SearchInput from 'components/search_sidebar/sidebar_search';

import type { CandidateDocument } from './types';

export const PickerMode = { Add: 'add', Remove: 'remove' } as const;

export type PickerMode = (typeof PickerMode)[keyof typeof PickerMode];

interface Props {
  // Upload starts with nothing chosen and that is a valid outcome: the page
  // lands in the library unattached.
  allowEmpty?: boolean;
  // Rendered above the checklist: upload puts its file summary and name fields
  // here so the checklist, search and orientation rules stay in one component.
  children?: React.ReactNode;
  documents: CandidateDocument[];
  mode: PickerMode;
  pageOrientation: string;
  saving: boolean;
  show: boolean;
  // Bulk add: why a document is unavailable for the whole selection. Replaces the
  // single-page orientation rule when given.
  mismatchReason?: (doc: CandidateDocument) => string | null;
  // Upload confirms with nothing selected, where a bare "Add" reads as a no-op.
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (documentIds: number[]) => void;
}

export const orientationMismatch = (doc: CandidateDocument, pageOrientation: string) => {
  if (!doc.orientation) return false;
  if (pageOrientation === 'unknown' || pageOrientation === 'mixed') return false;

  return doc.orientation !== pageOrientation;
};

export default function DocumentPicker({
  allowEmpty = false, children, documents, mismatchReason, mode, pageOrientation, saving, show, submitLabel,
  onClose, onSubmit,
}: Props) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const candidates = useMemo(
    () => documents.filter(doc => (mode === 'add' ? !doc.in_document : doc.in_document)),
    [documents, mode]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? candidates.filter(doc => doc.name.toLowerCase().includes(term)) : candidates;
  }, [candidates, search]);

  const groups = useMemo(() => {
    const byType = new Map<string, CandidateDocument[]>();
    visible.forEach((doc) => {
      const key = doc.document_type || 'Other';
      byType.set(key, [...(byType.get(key) || []), doc]);
    });
    return [...byType.entries()];
  }, [visible]);

  const reasonFor = (doc: CandidateDocument): string | null => {
    if (mode === 'remove') return null;
    if (mismatchReason) return mismatchReason(doc);
    if (orientationMismatch(doc, pageOrientation)) {
      return `${doc.orientation} document; this page is ${pageOrientation}`;
    }

    return null;
  };

  const selectable = visible.filter(doc => reasonFor(doc) === null);

  const toggle = (id: number, checked: boolean) => {
    setSelectedIds(current => (checked ? [...current, id] : current.filter(i => i !== id)));
  };

  const selectAllVisible = () => {
    setSelectedIds(current => [...new Set([...current, ...selectable.map(doc => doc.id)])]);
  };

  const close = () => {
    if (saving) return;

    setSelectedIds([]);
    setSearch('');
    onClose();
  };

  return (
    <Modal open={show} onClose={close}>
      <Modal.Content centered scrollable>
        <Modal.Header>
          <Modal.Title>{mode === 'add' ? 'Add to documents' : 'Remove from documents'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {children}
          <div className="d-flex align-items-center gap-3 mb-3">
            <SearchInput
              placeholder="Search documents"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
            />
            <Button size="sm" variant="ghost" onClick={selectAllVisible}>
              Select all
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
              Clear
            </Button>
          </div>
          {groups.map(([type, docs]) => (
            <div className="mb-3" key={type}>
              <div className="fw-semibold text-muted mb-1">{type}</div>
              {docs.map((doc) => {
                const reason = reasonFor(doc);
                return (
                  <div className="d-flex align-items-center justify-content-between py-1" key={doc.id}>
                    <Checkbox
                      checked={selectedIds.includes(doc.id)}
                      disabled={reason !== null}
                      label={doc.name}
                      onCheckedChange={(checked: boolean | 'indeterminate') => toggle(doc.id, checked === true)}
                    />
                    {reason && <span className="text-muted fs-small">{reason}</span>}
                  </div>
                );
              })}
            </div>
          ))}
          {visible.length === 0 && <p className="text-muted mb-0">No documents match.</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving} variant="ghost" onClick={close}>Cancel</Button>
          <Button
            disabled={saving || (selectedIds.length === 0 && !allowEmpty)}
            variant="primary"
            onClick={() => onSubmit(selectedIds)}
          >
            {submitLabel || (mode === 'add' ? 'Add' : 'Remove')}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
